import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Button,
  Loader,
  Spacing,
  Title,
  Paragraph,
  WithFeedbackProps,
  withFeedback,
  Modal,
} from "@sicredi/react";

import { useLocation, useNavigate } from "react-router-dom";
import { getProductVisual } from "#/utils/dataForProducts";
import { PAGE } from "#/constants";
import { InputRange } from "#/components/InputRange";
import {
  findMinMaxByCodCommissioned,
  MinMaxData,
  MinMaxDataResponse,
} from "#/api/getMinMax";
import { ContextAPI } from "#/contextProver/ContextProver";
import { Footer } from "#/components/Footer";
import styles from "./styles.module.scss";
import {
  formatValueMoney,
  getIdURL,
  getIdComercialURL,
  toastErrorMenssage,
} from "#/contextProver/ContextProverServices";
import { getBens, TipoVenda } from "#/api/getBens";
import { ModalErroApi } from "#/components/ModalErroApi";
import { OfertaIndisponivelModal } from "#/components/AccountAgencyComponents/OfertaIndisponivelModal";
import { ButtonTabComponent } from "#/components/ButtonTabComponent";
import { SimulacaoOfertaIndisponivelModal } from "#/components/AccountAgencyComponents/SimulacaoOfertaIndisponivelModal";
import { AxiosError } from "axios";

export const Simulation: React.FC<WithFeedbackProps> = ({
  setError,
  setSucceeded,
  setException,
  clearFeedback,
}) => {
  const {
    codigoComissionado,
    valorParcela,
    setValorParcela,
    idProduto,
    setIdProduto,
    idSubProduto,
    idTipoVenda,
    situacaoGrupo,
    setBens,
    valorBem,
    setValorBem,
    documento,
    isLoading,
    setIsLoading,
    setIsOpenOfertaIndisponivelModal,
    isOpenOfertaIndisponivelModal,
    vendasListaProposta,
    products,
  } = useContext(ContextAPI);
  const navigate = useNavigate();
  const { search } = useLocation();
  const idURL = getIdURL(search);
  const idComercialURL = getIdComercialURL(search);

  const currentProduct = products?.find(
    (item) => item.idProduto === idProduto
  );

  const [valueMaxMin, setValueMaxMin] = useState<MinMaxData | undefined>(
    undefined
  );
  const [valueBemFormatado, setValueBemFormatado] = useState("");
  const [valueParcelaFormatado, setValueParcelaFormatado] = useState("");
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isIncompatibleMinValueError, setIsIncompatibleMinValueError] =
    useState<boolean>(false);
  const [
    isSimulacaoOpenOfertaIndisponivelModal,
    setIsSimulacaoOpenOfertaIndisponivelModal,
  ] = useState(false);

  const [isCreditoParametroBens, setIsCreditoParametroBens] = useState(true);
  const [isParcelaParametroBens, setIsParcelaParametroBens] = useState(false);

  const iniciaResetValoresSimulacao = (valueMaxMin: MinMaxData) => {
    setValorBem(valueMaxMin.vl_bem_minimo);
    setValorParcela(valueMaxMin.vl_pc_minimo);
    setValueBemFormatado(formatValueMoney(valueMaxMin.vl_bem_minimo));
    setValueParcelaFormatado(formatValueMoney(valueMaxMin.vl_pc_minimo));
  };

  const proposalListIsEmpty = useMemo(() => {
    return !vendasListaProposta?.data.some(
      (item) =>
        item.dataCancelamentoProposta === "0001-01-01 00:00:00" &&
        item.boletoPago === "N"
    );
  }, [vendasListaProposta]);

  const getValueMaxMin = async () => {
    try {
      setIsLoading(true);
      const data = await findMinMaxByCodCommissioned({
        codigoComissionado,
        idProduto,
        documento,
      });
      if (data) {
        setValueMaxMin(data.data);
        iniciaResetValoresSimulacao(data.data);
      }
      setIsLoading(false);
    } catch (error) {
      const { response } = error as AxiosError<MinMaxDataResponse>;

      if (
        response?.data.errors.message ===
        "Não foram encontrados bens para simulação de acordo com o valor informado em VL_PC_BEM."
      ) {
        setIsIncompatibleMinValueError(true);
      } else if (!idURL) {
        toastErrorMenssage();
      }

      setIsLoading(false);
    }
  };

  const bensApiParcelaCredito = async () => {
    if (idSubProduto) {
      try {
        if (isParcelaParametroBens) {
          const bensApi = await getBens({
            codigoComissionado,
            idProduto,
            idSubProduto,
            valorCredito: valorParcela,
            documento,
            tipoCredito: TipoVenda.parcela,
            idTipoVenda,
            situacaoGrupo,
          });
          return bensApi;
        }
        if (isCreditoParametroBens) {
          const bensApi = await getBens({
            codigoComissionado,
            idProduto,
            idSubProduto,
            valorCredito: valorBem,
            documento,
            tipoCredito: TipoVenda.credito,
            idTipoVenda,
            situacaoGrupo,
          });
          return bensApi;
        }
      } catch {
        setIsOpenOfertaIndisponivelModal(true);
      }
    }
  };

  const getBensData = async () => {
    try {
      setIsLoading(true);

      const bensApi = await bensApiParcelaCredito();

      if (bensApi) {
        setBens({ ...bensApi });
        navigate(PAGE.ESCOLHA_DO_PLANO);
      } else {
        setIsOpenOfertaIndisponivelModal(true);
      }
    } catch {
      toastErrorMenssage();
    } finally {
      if (valueMaxMin) {
        iniciaResetValoresSimulacao(valueMaxMin);
      }
      setIsLoading(false);
    }
  };

  const handleButtonOpcoes = async () => {
    await getBensData();
  };

  const handleNavigateHome = () => {
    navigate(PAGE.ROOT);
  };

  const handleNavigateMenu = () => {
    navigate(PAGE.MENU_CONSORCIO);
  };

  useEffect(() => {
    if (idURL) {
      setIdProduto(Number(idURL));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idURL]);

  useEffect(() => {
    if (valueMaxMin) {
      setValorBem(valueMaxMin.vl_bem_minimo);
    }
    if (
      valueMaxMin?.vl_bem_minimo === 0 &&
      valueMaxMin?.vl_bem_maximo === 0 &&
      valueMaxMin?.vl_pc_minimo === 0 &&
      valueMaxMin?.vl_pc_maximo === 0
    ) {
      setIsSimulacaoOpenOfertaIndisponivelModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueMaxMin]);

  useEffect(() => {
    if (idProduto === Number(idURL)) {
      getValueMaxMin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codigoComissionado, idProduto, idURL]);

  const handleCreditoTabItem = () => {
    setIsCreditoParametroBens(true);
    setIsParcelaParametroBens(false);
  };
  const handleParcelaTabItem = () => {
    setIsParcelaParametroBens(true);
    setIsCreditoParametroBens(false);
  };

  return (
    <>
      <Loader show={isLoading} fullScreen />

      <Modal
        title="Oferta indisponível pelo comprometimento de renda"
        onClose={handleNavigateHome}
        show={isIncompatibleMinValueError}
        primaryButton={{
          children: "Revisar propostas",
          onClick: handleNavigateMenu,
          disabled: proposalListIsEmpty,
        }}
        secondaryButton={{
          children: "Entendi",
          onClick: handleNavigateHome,
        }}
      >
        Revise suas propostas pendentes para atingir a renda necessária ou tente
        outro produto.
      </Modal>

      {valueMaxMin && (
        <div className={styles.container}>
          <div className={styles.container_view}>
            <div className={styles.content_part_left_text_button}>
              <div>
                <Title as="h1" weight="semi-bold" className={styles.title}>
                  Escolha como deseja simular
                </Title>
                <Spacing appearance="xx-small" />
                <Paragraph style={{ color: "#808080" }}>
                  Você pode colocar um valor de crédito ou de parcelas
                  desejadas.
                </Paragraph>
                <Spacing appearance="large" />

                <div className={styles.container_button_tab}>
                  <ButtonTabComponent
                    text="Crédito"
                    onClick={() => handleCreditoTabItem()}
                    isParametroBens={isCreditoParametroBens}
                  />
                  <ButtonTabComponent
                    text="Parcela"
                    onClick={() => handleParcelaTabItem()}
                    isParametroBens={isParcelaParametroBens}
                  />
                </div>

                {isCreditoParametroBens && (
                  <InputRange
                    label="Valor do crédito"
                    min={valueMaxMin.vl_bem_minimo}
                    max={valueMaxMin.vl_bem_maximo}
                    id="credito"
                    value={valorBem}
                    valueFormat={valueBemFormatado}
                    setValue={setValorBem}
                    setValueFormat={setValueBemFormatado}
                  />
                )}
                {isParcelaParametroBens && (
                  <InputRange
                    label="Valor da parcela"
                    min={valueMaxMin.vl_pc_minimo}
                    max={valueMaxMin.vl_pc_maximo}
                    id="parcel"
                    value={valorParcela}
                    valueFormat={valueParcelaFormatado}
                    setValue={setValorParcela}
                    setValueFormat={setValueParcelaFormatado}
                  />
                )}

                <Spacing appearance="large" />

                <div className={styles.button_container}>
                  <Button
                    block={true}
                    appearance="secondary"
                    onClick={handleNavigateHome}
                    className={styles.button}
                  >
                    Voltar
                  </Button>

                  <Button
                    block={true}
                    appearance="primary"
                    onClick={handleButtonOpcoes}
                    className={styles.button}
                  >
                    Ver opções
                  </Button>
                </div>
              </div>
            </div>

            <div className={styles.img_container}>
              <img
                src={
                  getProductVisual(
                    idComercialURL
                      ? Number(idComercialURL)
                      : currentProduct?.idComercial
                  )?.imageSmall
                }
                alt="Imagem do Produto"
              />
            </div>
          </div>
          <div className={styles.footer_container}>
            <Footer paddingMobile />
          </div>
          <ModalErroApi
            messageErro="Não encontrados nenhuma opção para o produto selecionado."
            isOpenModal={isOpenModal}
            setIsOpenModal={setIsOpenModal}
          />
        </div>
      )}
      <OfertaIndisponivelModal
        isOpen={isOpenOfertaIndisponivelModal}
        setIsOpen={setIsOpenOfertaIndisponivelModal}
        isLogout={false}
      />
      <SimulacaoOfertaIndisponivelModal
        isOpen={isSimulacaoOpenOfertaIndisponivelModal}
        setIsOpen={setIsSimulacaoOpenOfertaIndisponivelModal}
      />
    </>
  );
};

export default withFeedback(Simulation);

